

















































































import { OrderProduct } from "@/model/OrderProduct";
import humanReadableQuantity from "@/filters/QuantityFormatter";
import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import { OrderState } from "@/model/OrderState";
import { ModifiedQuantity } from "@/model/order/ModifiedQuantity";
import { OrderedProductStatuses } from "@/model/OrderedProductStatus";
import { Quantity } from "@/model/Quantity";
import ChangeQuantity from "@/components/orders/ChangeQuantity.vue";
import { SerializationHelper } from "@/helpers/SerializationHelper";
import {SupportProduct} from "@/model/support/SupportProduct";
import MoreActionsDialog from "@/components/dialog-box/MoreActionsDialog.vue";
import OutOfStockDialog from "@/components/dialog-box/OutOfStockDialog.vue";
import {EventBus} from "@/helpers/EventBus";

@Component({
    components : { MoreActionsDialog, ChangeQuantity },
	filters: { humanReadableQuantity },
})
export default class ProductLinePanel extends Vue {
    @Prop({ default: null }) product: OrderProduct;
    @Prop({ default: null }) supportProduct: SupportProduct;
    @Prop({ default: false }) showQuantityInput: boolean;
    @Prop({ default: OrderState.FAILED }) orderStatus: OrderState;
    @Prop({ default: false }) showActionOnProduct: boolean;
    @Prop({ default: false }) showEditOnProduct: boolean;
    @Prop({ default: false }) isOutOfStock: boolean;
    @Prop({ default: 0 }) otherSupportsPreparedQuantity: number;

    localShowQuantityInput: boolean = false;

    public modifiedQuantity: ModifiedQuantity = new ModifiedQuantity();

    public isActionsMenuOpen = false;

    @Watch("showQuantityInput")
    onShowQuantityInputChanged(value: boolean): void {
      this.localShowQuantityInput = value;

      if (value) {
        this.modifiedQuantity.quantity = this.initialQuantity;
        this.modifiedQuantity.cug = this.product?.cug;
        this.$emit("onValueChange", this.modifiedQuantity);
      }
    }

    mounted() {
        this.modifiedQuantity.quantity = this.initialQuantity;
        this.modifiedQuantity.cug = this.product?.cug;
        this.localShowQuantityInput = this.showQuantityInput;

        this.$emit("onValueChange", this.modifiedQuantity);
    }

    onQuantityChange(quantity: Quantity): void {
      this.modifiedQuantity.quantity = quantity;
      this.$emit("onValueChange", this.modifiedQuantity);
    }

    onProductAction(): void {
        this.$emit("productaction", { product: this.product });
    }

    onProductEdit(): void {
      EventBus.$emit(EventBus.EDIT_SCANNABLE_PRODUCT, this.product, this.supportProduct);
    }

    onSubstitutionClick(): void {
        if (this.product.isSubstituted) {
          return;
        }

        this.isActionsMenuOpen = false;
        this.$emit("substitute", { product: this.product });
    }

    onOutOfStockClick(): void {
      this.isActionsMenuOpen = false;

      this.$emit("handleOutOfStock", { product: this.product });
    }

    toggleActionsMenu(): void {
        this.isActionsMenuOpen = !this.isActionsMenuOpen;
    }

    closeActionsMenu(): void {
        this.isActionsMenuOpen = false;
    }

    get showEditButton(): boolean {
      return this.showEditOnProduct && this.product.isScannable;
    }

    get initialQuantity(): Quantity {
      if (this.isOutOfStock) {
        return new Quantity(0, this.product?.quantity?.unit);
      }

      if (this.supportProduct != null) {
        return new Quantity(this.supportProduct.processedQuantity, this.product?.quantity?.unit);
      }

      return this.product?.processedQuantity != null && this.product?.processedQuantity.value > 0 ?
        SerializationHelper.toInstanceFromJsonObj(new Quantity(), this.product?.processedQuantity) :
        SerializationHelper.toInstanceFromJsonObj(new Quantity(), this.product?.quantity);
    }

    get maxQuantity(): number | null {
      if (!this.hasProductQuantity) {
        return null;
      }

      if (this.isOutOfStock) {
        const alreadyPrepared = this.product?.processedQuantity?.value ?? 0;
        return Math.max(0, this.product.quantity.value - alreadyPrepared);
      }

      if (this.supportProduct != null) {
        const otherSupportsQty = this.otherSupportsPreparedQuantity ?? 0;
        return Math.max(0, this.product.quantity.value - otherSupportsQty);
      }

      return this.product.quantity.value;
    }

    get thumbnail(): string {
        return this.supportProduct ? this.supportProduct.thumbnail : this.product.thumbnail;
    }

    get label(): string {
        return this.supportProduct ? this.supportProduct.label : this.product.label;
    }

    get cug (): string {
        return this.supportProduct ? this.supportProduct.cug : this.product.cug;
    }

    get origin (): string {
        return this.supportProduct ? this.supportProduct.origin : this.product.origin;
    }

    get codePlu(): string {
      return this.product?.codePlu?.trim();
    }

    get quantity(): Quantity {
      return this.product?.quantity;
    }

    get hasOrigin(): boolean {
        return this.product?.origin != null && this.product?.origin !== '';
    }

    get hasCodePlu(): boolean {
        return this.product?.codePlu != null && this.product.codePlu.trim() !== '';
    }

    get hasMissingQuantity(): boolean {
      if (!this.hasProductQuantity || !this.hasProcessedQuantity) {
        return false;
      }

      const processedQuantity = this.product?.processedQuantity?.value ?? this.supportProduct?.processedQuantity;

      return this.product?.quantity.value !== processedQuantity;

    }

    get isProductProcessed(): boolean {
        return this.product?.status === OrderedProductStatuses.PREPARED;
    }

    get isProductOutOfStock(): boolean {
      return this.product?.processedQuantity != this.product?.quantity;
    }

    get hasProductQuantity(): boolean {
        return this.product != null && this.product.quantity != null;
    }

    get processedQuantity(): Quantity {
        if(this.supportProduct != null) {
          return new Quantity(this.supportProduct.processedQuantity);
        }

        if (this.hasProcessedQuantity) {
            return this.product?.processedQuantity;
        }

        if (this.hasProductQuantity) {
            return this.product?.quantity;
        }

        return new Quantity();
    }

    get hasProcessedQuantity(): boolean{
      if(this.supportProduct != null) {
        return this.supportProduct.processedQuantity != null;
      }
      return this.product != null && this.product.processedQuantity != null;
    }

    get hasModifiedQuantity(): boolean {
        return this.modifiedQuantity != null && this.modifiedQuantity.quantity != null;
    }

    get isModifiedQuantityInferior(): boolean {
        if (!this.hasProductQuantity) {
            return false;
        }

        if (!this.hasModifiedQuantity) {
            return false;
        }

        return this.prospectiveTotalQuantity < this.product.quantity.value;
    }

    get prospectiveTotalQuantity(): number {
        const modifiedValue = this.modifiedQuantity.quantity?.value ?? 0;

        if (this.supportProduct != null) {
            return modifiedValue + (this.otherSupportsPreparedQuantity ?? 0);
        }

        if (this.isOutOfStock) {
            return (this.product?.processedQuantity?.value ?? 0) + modifiedValue;
        }

        return modifiedValue;
    }

    get canDisplayOrderedQuantity(): boolean {
        if (this.supportProduct != null) {
            return false;
        }

        return (!this.isProductProcessed || !this.hasProcessedQuantity) && this.hasProductQuantity;
    }

    get canDisplayProcessedQuantity(): boolean {
        if (this.supportProduct != null) {
            return this.hasProcessedQuantity;
        }

        return this.isProductProcessed && this.hasProcessedQuantity;
    }

    get canDisplayMissingQuantity(): boolean {
        return this.isProductProcessed && this.hasProcessedQuantity && this.hasMissingQuantity;
    }

    get canDisplaySubstitutedFlag(): boolean {
        return this.product?.isSubstituted;
    }
}
